import { useMemo, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { Download } from 'lucide-react';

import Card from '@/components/ui/Card';
import Badge from '@/components/ui/Badge';
import Spinner from '@/components/ui/Spinner';
import Table, { sortRows, type Column, type SortState } from '@/components/ui/Table';
import { BarList, DonutChart, Heatmap, LineChart, SERIES_COLOURS } from '@/components/engagement/Charts';
import { getEngagementData, type EngagementData, type EngagementUser } from '@/api/engagement';
import {
  MAX_RANGE_DAYS,
  addDays,
  averageMinutesByWeekday,
  compare,
  daysInclusive,
  formatMinutes,
  toCsv,
  todayIn,
  topWithOther,
  weekdayName,
} from '@/utils/engagement';

type Preset = 7 | 30 | 90 | 'custom';

const ROLE_LABELS: Record<string, string> = { ADMIN: 'Admin', GROUP_FINANCE: 'Group Finance', USER: 'User' };
const STATUS: Record<EngagementUser['status'], { label: string; variant: 'green' | 'amber' | 'grey' }> = {
  active: { label: 'Active', variant: 'green' },
  dormant: { label: 'Dormant', variant: 'amber' },
  never_logged_in: { label: 'Never logged in', variant: 'grey' },
};

const formatDate = (iso: string) =>
  new Date(`${iso}T00:00:00Z`).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' });

const formatLastSeen = (iso: string | null) =>
  iso
    ? new Date(iso).toLocaleString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        timeZone: 'Europe/London',
      })
    : 'Never';

// ---------------------------------------------------------------------------
// Tiles
// ---------------------------------------------------------------------------

function Tile({
  title,
  value,
  current,
  previous,
  lowerIsBetter = false,
  note,
}: {
  title: string;
  value: string;
  current: number;
  previous: number;
  lowerIsBetter?: boolean;
  note?: string;
}) {
  const change = compare(current, previous);
  const good = change.direction === 'flat' ? null : (change.direction === 'up') !== lowerIsBetter;
  return (
    <Card className="!p-4">
      <p className="text-xs font-medium text-gray-500">{title}</p>
      <p className="mt-1 text-2xl font-bold tabular-nums text-gray-900">{value}</p>
      <p
        className={clsx(
          'mt-1 text-xs tabular-nums',
          good === null ? 'text-gray-500' : good ? 'text-green-700' : 'text-red-700',
        )}
        title={`Previous period: ${previous}`}
      >
        {change.direction === 'flat' ? '– no change' : `${change.direction === 'up' ? '▲' : '▼'} ${change.pct === null ? 'new' : `${Math.abs(change.pct)}%`}`}
        <span className="text-gray-400"> vs previous</span>
      </p>
      {note && <p className="mt-1 text-[11px] text-gray-400">{note}</p>}
    </Card>
  );
}

function Panel({ title, subtitle, children, className }: { title: string; subtitle?: string; children: ReactNode; className?: string }) {
  return (
    <Card className={clsx('min-w-0', className)}>
      <h2 className="text-sm font-semibold text-gray-900">{title}</h2>
      {subtitle && <p className="mt-0.5 text-xs text-gray-500">{subtitle}</p>}
      <div className="mt-4">{children}</div>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// User table
// ---------------------------------------------------------------------------

const userColumns: Column<EngagementUser>[] = [
  {
    key: 'name',
    header: 'Name',
    sortable: true,
    sortValue: (u) => u.name,
    render: (u) => (
      <div className="min-w-0">
        <p className="font-medium text-gray-900">{u.name}</p>
        {u.name !== u.email && <p className="text-xs text-gray-500">{u.email}</p>}
      </div>
    ),
  },
  { key: 'role', header: 'Role', sortable: true, sortValue: (u) => ROLE_LABELS[u.role] ?? u.role, render: (u) => ROLE_LABELS[u.role] ?? u.role },
  {
    key: 'lastSeen',
    header: 'Last seen',
    sortable: true,
    sortValue: (u) => u.lastSeen ?? '',
    className: 'whitespace-nowrap',
    render: (u) => formatLastSeen(u.lastSeen),
  },
  { key: 'logins', header: 'Logins', sortable: true, sortValue: (u) => u.logins, className: 'text-right tabular-nums', render: (u) => u.logins },
  { key: 'sessions', header: 'Sessions', sortable: true, sortValue: (u) => u.sessions, className: 'text-right tabular-nums', render: (u) => u.sessions },
  {
    key: 'estimatedMinutes',
    header: 'Est. time',
    sortable: true,
    sortValue: (u) => u.estimatedMinutes,
    className: 'text-right tabular-nums whitespace-nowrap',
    render: (u) => formatMinutes(u.estimatedMinutes),
  },
  { key: 'transactions', header: 'Transactions', sortable: true, sortValue: (u) => u.transactions, className: 'text-right tabular-nums', render: (u) => u.transactions },
  {
    key: 'status',
    header: 'Status',
    sortable: true,
    sortValue: (u) => STATUS[u.status].label,
    render: (u) => <Badge variant={STATUS[u.status].variant}>{STATUS[u.status].label}</Badge>,
  },
];

function downloadCsv(filename: string, csv: string) {
  const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function UserTable({ data }: { data: EngagementData }) {
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<SortState | null>({ key: 'lastSeen', dir: 'desc' });

  const term = search.trim().toLowerCase();
  const shown = sortRows(
    data.byUser.filter(
      (u) =>
        !term ||
        [u.name, u.email, ROLE_LABELS[u.role] ?? u.role, STATUS[u.status].label].some((v) => v.toLowerCase().includes(term)),
    ),
    userColumns,
    sort,
  );

  const exportCsv = () => {
    const csv = toCsv(
      ['Name', 'Email', 'Role', 'Last seen', 'Logins', 'Sessions', 'Estimated minutes', 'Transactions', 'Status'],
      shown.map((u) => [
        u.name,
        u.email,
        ROLE_LABELS[u.role] ?? u.role,
        u.lastSeen ? formatLastSeen(u.lastSeen) : 'Never',
        u.logins,
        u.sessions,
        Math.round(u.estimatedMinutes),
        u.transactions,
        STATUS[u.status].label,
      ]),
    );
    downloadCsv(`engagement-users-${data.range.from}-to-${data.range.to}.csv`, csv);
  };

  return (
    <section className="space-y-3">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h2 className="text-sm font-semibold text-gray-900">Users</h2>
          <p className="text-xs text-gray-500">
            Enabled users. Figures are for the selected range; last seen and status are as at its end.
          </p>
        </div>
        <div className="flex gap-2">
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search users"
            aria-label="Search users"
            className="input-field sm:w-56"
          />
          <button type="button" className="btn-secondary whitespace-nowrap" onClick={exportCsv} disabled={shown.length === 0}>
            <Download className="h-4 w-4" aria-hidden />
            CSV
          </button>
        </div>
      </div>
      <Table
        columns={userColumns}
        data={shown}
        rowKey={(u) => u.userId}
        sort={sort}
        onSortChange={setSort}
        emptyMessage={term ? 'No users match your search.' : 'No enabled users.'}
      />
    </section>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export default function EngagementPage() {
  const today = todayIn();
  const [preset, setPreset] = useState<Preset>(30);
  const [customFrom, setCustomFrom] = useState(addDays(today, -29));
  const [customTo, setCustomTo] = useState(today);
  const [includeExcluded, setIncludeExcluded] = useState(false);

  const customError =
    preset !== 'custom'
      ? null
      : !customFrom || !customTo
        ? 'Choose both dates.'
        : customFrom > customTo
          ? 'The start date must be on or before the end date.'
          : daysInclusive(customFrom, customTo) > MAX_RANGE_DAYS
            ? 'Custom ranges are limited to 12 months.'
            : null;

  const range = useMemo(
    () =>
      preset === 'custom'
        ? { from: customFrom, to: customTo }
        : { from: addDays(today, -(preset - 1)), to: today },
    [preset, customFrom, customTo, today],
  );

  const { data, isLoading, isError, isFetching } = useQuery({
    queryKey: ['engagement', range.from, range.to, includeExcluded],
    queryFn: () => getEngagementData({ ...range, includeExcluded }),
    enabled: !customError,
    staleTime: 60_000,
    placeholderData: (prev) => prev,
  });

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Engagement</h1>
        <p className="mt-1 text-sm text-gray-500">
          Is the app being used, when, and by whom. Counts only — no cash flow figures are shown here.
        </p>
      </div>

      {/* Controls */}
      <Card className="!p-4">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
          <div className="flex flex-wrap items-end gap-3">
            <div role="group" aria-label="Date range" className="inline-flex overflow-hidden rounded-lg border border-gray-300">
              {([7, 30, 90, 'custom'] as Preset[]).map((p) => (
                <button
                  key={p}
                  type="button"
                  onClick={() => setPreset(p)}
                  aria-pressed={preset === p}
                  className={clsx(
                    'px-3 py-2 text-sm font-medium',
                    preset === p ? 'bg-primary-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50',
                  )}
                >
                  {p === 'custom' ? 'Custom' : `${p} days`}
                </button>
              ))}
            </div>
            {preset === 'custom' && (
              <div className="flex flex-wrap items-end gap-2">
                <label className="text-xs text-gray-600">
                  From
                  <input type="date" className="input-field mt-1" value={customFrom} max={customTo} onChange={(e) => setCustomFrom(e.target.value)} />
                </label>
                <label className="text-xs text-gray-600">
                  To
                  <input type="date" className="input-field mt-1" value={customTo} min={customFrom} onChange={(e) => setCustomTo(e.target.value)} />
                </label>
              </div>
            )}
          </div>
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              checked={includeExcluded}
              onChange={(e) => setIncludeExcluded(e.target.checked)}
            />
            Include excluded accounts
          </label>
        </div>
        {customError && <p className="mt-2 text-xs text-red-600">{customError}</p>}
        {data && (
          <p className="mt-3 text-xs text-gray-500">
            {formatDate(data.range.from)} – {formatDate(data.range.to)} compared with{' '}
            {formatDate(data.previousRange.from)} – {formatDate(data.previousRange.to)} (UK time).
            {!data.definitions.includeExcluded && data.excludedAccounts > 0 &&
              ` ${data.excludedAccounts} test/support account${data.excludedAccounts === 1 ? '' : 's'} excluded.`}
            {isFetching && ' Updating…'}
          </p>
        )}
      </Card>

      {isLoading && (
        <div className="flex justify-center py-12">
          <Spinner size="lg" />
        </div>
      )}
      {isError && !data && (
        <Card>
          <p className="text-sm text-red-700">The engagement figures could not be loaded. Try again shortly.</p>
        </Card>
      )}
      {data && !customError && <Dashboard data={data} />}
    </div>
  );
}

function Dashboard({ data }: { data: EngagementData }) {
  const { totals: t, previous: p } = data;
  const hasActivity = data.daily.some((d) => d.activeUsers > 0);
  const dates = data.daily.map((d) => d.date);
  const weekdayAvg = averageMinutesByWeekday(data.daily);
  const loginSlices = topWithOther(data.byUser, (u) => u.logins, (u) => u.name, 8);
  const txByUser = [...data.byUser]
    .filter((u) => u.transactions > 0)
    .sort((a, b) => b.transactions - a.transactions)
    .slice(0, 10);

  return (
    <>
      {/* Row 1 — headline tiles */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-7">
        <Tile title="Logins" value={String(t.logins)} current={t.logins} previous={p.logins} />
        <Tile title="Active users" value={String(t.activeUsers)} current={t.activeUsers} previous={p.activeUsers} />
        <Tile title="Stickiness" value={`${t.stickinessPct}%`} current={t.stickinessPct} previous={p.stickinessPct} note="Avg DAU ÷ MAU" />
        <Tile title="Estimated time in use" value={formatMinutes(t.estimatedMinutes)} current={t.estimatedMinutes} previous={p.estimatedMinutes} note="Estimated" />
        <Tile title="Transactions" value={String(t.transactions)} current={t.transactions} previous={p.transactions} />
        <Tile title="Dormant users" value={String(t.dormantUsers)} current={t.dormantUsers} previous={p.dormantUsers} lowerIsBetter />
        <Tile title="Never logged in" value={String(t.neverLoggedIn)} current={t.neverLoggedIn} previous={p.neverLoggedIn} lowerIsBetter />
      </div>

      {!hasActivity ? (
        <Card>
          <p className="text-sm text-gray-600">
            No activity was recorded between {formatDate(data.range.from)} and {formatDate(data.range.to)}.
          </p>
        </Card>
      ) : (
        <>
          {/* Row 2 — is it being used? */}
          <div className={clsx('grid grid-cols-1 gap-4', data.daily.length >= 90 && 'xl:grid-cols-2')}>
            <Panel title="Daily activity" subtitle="Logins, distinct active users and transactions per day">
              <LineChart
                label="Daily logins, active users and transactions"
                dates={dates}
                series={[
                  { label: 'Logins', colour: SERIES_COLOURS[0], values: data.daily.map((d) => d.logins) },
                  { label: 'Active users', colour: SERIES_COLOURS[1], values: data.daily.map((d) => d.activeUsers) },
                  { label: 'Transactions', colour: SERIES_COLOURS[2], values: data.daily.map((d) => d.transactions) },
                ]}
              />
            </Panel>
            {data.daily.length >= 90 && (
              <Panel title="Weekly active users" subtitle="Distinct active users in the 7 days ending each day">
                <LineChart
                  label="Weekly active users"
                  dates={dates}
                  series={[{ label: 'Weekly active users', colour: SERIES_COLOURS[4], values: data.daily.map((d) => d.wau) }]}
                />
              </Panel>
            )}
          </div>

          {/* Row 3 — when? */}
          <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
            <Panel title="When people use it" subtitle="Activity by day of week and hour (UK time)" className="xl:col-span-2">
              <Heatmap label="Activity by day of week and hour (UK time)" cells={data.heatmap} />
            </Panel>
            <Panel title="Average time per day of week" subtitle="Estimated minutes, by the day a session started">
              <BarList
                label="Average estimated minutes per day of week"
                items={weekdayAvg.map((d) => ({ label: weekdayName(d.weekday), value: Math.round(d.avgMinutes) }))}
                format={formatMinutes}
              />
            </Panel>
          </div>

          {/* Row 4 — by whom? */}
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <Panel title="Logins by user" subtitle="Top 8 users; the rest grouped as Other">
              {loginSlices.length ? (
                <DonutChart label="Logins by user" slices={loginSlices} />
              ) : (
                <p className="text-sm text-gray-500">No logins in this range.</p>
              )}
            </Panel>
            <Panel title="Transactions by user" subtitle="Top 10">
              {txByUser.length ? (
                <BarList
                  label="Transactions by user"
                  items={txByUser.map((u) => ({ label: u.name, value: u.transactions }))}
                  colour={SERIES_COLOURS[2]}
                />
              ) : (
                <p className="text-sm text-gray-500">No transactions in this range.</p>
              )}
            </Panel>
          </div>

          {/* Row 5 — what? */}
          <Panel title="Transactions by action" subtitle="Changes recorded in the audit trail; unused actions show as zero">
            <BarList
              label="Transactions by action"
              items={data.byFeature.map((f) => ({ label: `${f.area}: ${f.feature}`, value: f.transactions }))}
              colour={SERIES_COLOURS[5]}
            />
          </Panel>
        </>
      )}

      {/* Row 6 — users */}
      <UserTable data={data} />

      {/* Not recorded + definitions */}
      <Card className="!p-4">
        <h2 className="text-sm font-semibold text-gray-900">About these figures</h2>
        <ul className="mt-2 list-disc space-y-1 pl-5 text-xs text-gray-600">
          <li>
            <strong>Failed logins: Not recorded.</strong> The app does not keep a record of failed sign-in attempts, so there is no security panel.
          </li>
          <li>
            <strong>Groups: Not recorded.</strong> Users have no single organisation, division or site, so there is no group filter.
          </li>
          <li>
            A session is a run of a user's activity with no gap over {data.definitions.inactivityGapMinutes} minutes. Estimated time adds{' '}
            {data.definitions.sessionAllowanceMinutes} minutes to each session and is an estimate, not a measurement.
          </li>
          <li>
            Activity is sign-ins, token refreshes while the app is open, and changes in the audit trail. Transactions are audit-trail changes
            (adding, changing or removing a line; submitting, agreeing, reopening).
          </li>
          <li>
            Dormant: an enabled user whose last activity was more than {data.definitions.dormantAfterDays} days before the end of the range.
            Never logged in takes precedence.
          </li>
          <li>Excluded by default: {data.definitions.excludedRule}</li>
        </ul>
      </Card>
    </>
  );
}
